import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router";
import RootLayout from "@/layouts/RootLayout";
import AdminLayout from "@/layouts/AdminLayout";
import AuthLayout from "@/layouts/AuthLayout";
import Login from "@/pages/auth/Login";
import ResetPassword from "@/pages/auth/ResetPassword";
import RecoveryRedirect from "@/components/auth/RecoveryRedirect";
import { RouteLoading } from "@/components/loading/RouteLoading";
import PrivateRoute from "@/components/auth/PrivateRoute";
import { AdminErrorBoundary } from "@/lib/errors/boundary";
import SEO from "@/components/seo/SEO";

const Home = lazy(() => import("@/pages/Home"));
const WorksCreative = lazy(() => import("@/pages/WorksCreative"));
const WorkSingle = lazy(() => import("@/pages/WorkSingle"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogSingle = lazy(() => import("@/pages/BlogSingle"));
const ErrorTest =
  import.meta.env.VITE_ENABLE_ERROR_TEST === "true"
    ? lazy(() => import("@/pages/test/ErrorTest"))
    : null;
const NotFound = lazy(() => import("@/pages/NotFound"));

const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const SkillsAdmin = lazy(() => import("@/pages/admin/SkillsAdmin"));
const ProjectsAdmin = lazy(() => import("@/pages/admin/ProjectsAdmin"));
const ProjectCreateAdmin = lazy(
  () => import("@/pages/admin/ProjectCreateAdmin"),
);
const ProjectEditAdmin = lazy(() => import("@/pages/admin/ProjectEditAdmin"));
const HeroAdmin = lazy(() => import("@/pages/admin/HeroAdmin"));
const Settings = lazy(() => import("@/pages/admin/Settings"));
const BlogAdmin = lazy(() => import("@/pages/admin/BlogAdmin"));
const BlogCreateAdmin = lazy(() => import("@/pages/admin/BlogCreateAdmin"));
const BlogEditAdmin = lazy(() => import("@/pages/admin/BlogEditAdmin"));

// Wrapper for admin routes with Suspense fallback
function AdminSuspense({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteLoading />}>{children}</Suspense>;
}

function PublicSuspense({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteLoading />}>{children}</Suspense>;
}

function ErrorTestRoute() {
  if (!ErrorTest) return null;

  return (
    <>
      <SEO title="Error Test" noindex />
      <ErrorTest canThrowRenderError />
    </>
  );
}

function App() {
  return (
    <>
      <RecoveryRedirect />
      <Routes>
      <Route
        path="/login"
        element={
          <AuthLayout>
            <Login />
          </AuthLayout>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route
        path="/admin"
        element={
          <AdminErrorBoundary>
            <PrivateRoute>
              <AdminLayout />
            </PrivateRoute>
          </AdminErrorBoundary>
        }
      >
        <Route
          index
          element={
            <AdminSuspense>
              <Dashboard />
            </AdminSuspense>
          }
        />
        <Route
          path="skills"
          element={
            <AdminSuspense>
              <SkillsAdmin />
            </AdminSuspense>
          }
        />
        <Route
          path="projects"
          element={
            <AdminSuspense>
              <ProjectsAdmin />
            </AdminSuspense>
          }
        />
        <Route
          path="projects/new"
          element={
            <AdminSuspense>
              <ProjectCreateAdmin />
            </AdminSuspense>
          }
        />
        <Route
          path="projects/:id"
          element={
            <AdminSuspense>
              <ProjectEditAdmin />
            </AdminSuspense>
          }
        />
        <Route
          path="blog"
          element={
            <AdminSuspense>
              <BlogAdmin />
            </AdminSuspense>
          }
        />
        <Route
          path="blog/create"
          element={
            <AdminSuspense>
              <BlogCreateAdmin />
            </AdminSuspense>
          }
        />
        <Route
          path="blog/edit/:id"
          element={
            <AdminSuspense>
              <BlogEditAdmin />
            </AdminSuspense>
          }
        />
        <Route
          path="hero"
          element={
            <AdminSuspense>
              <HeroAdmin />
            </AdminSuspense>
          }
        />
        <Route
          path="settings"
          element={
            <AdminSuspense>
              <Settings />
            </AdminSuspense>
          }
        />
      </Route>

      <Route path="/" element={<RootLayout />}>
        <Route
          index
          element={
            <PublicSuspense>
              <Home />
            </PublicSuspense>
          }
        />
        <Route
          path="project"
          element={
            <PublicSuspense>
              <WorksCreative />
            </PublicSuspense>
          }
        />
        <Route
          path="project/:slug"
          element={
            <PublicSuspense>
              <WorkSingle />
            </PublicSuspense>
          }
        />
        <Route
          path="blog"
          element={
            <PublicSuspense>
              <Blog />
            </PublicSuspense>
          }
        />
        <Route
          path="blog/:slug"
          element={
            <PublicSuspense>
              <BlogSingle />
            </PublicSuspense>
          }
        />
      </Route>

      {ErrorTest && (
        <Route
          path="/test/error"
          element={
            <PublicSuspense>
              <ErrorTestRoute />
            </PublicSuspense>
          }
        />
      )}

      <Route
        path="*"
        element={
          <PublicSuspense>
            <NotFound />
          </PublicSuspense>
        }
      />
    </Routes>
    </>
  );
}

export default App;
